import React, { useState } from "react";
import { Link } from "react-router-dom";
import Table from "../../Components/Table";
import StatusHandler from "../../Components/Table/StatusHandler";
import { Confirmation, Success, Error } from "../../Components/Modal";
import ModalImageViewer from "../../Components/ModalImageViewer";
import useTableControls from "../../Hooks/useTableControls";
import useFetchData from "../../Hooks/useFetchData";
import { getPromoCodes, handlePromoCodeStatus, deletePromoCode } from "../../Services/PromoCode";
import { useMutation } from "react-query";
import { format_date, getSerialNumber } from "../../Utils/helpers";
import PageHeader from "../../Components/PageHeader";
import { image_url } from "../../Utils/connection_strings";

export default function PromoCodes() {
    const {
        perPage,
        setPerPage,
        status,
        setStatus,
        search_string,
        setSearchString,
        from,
        setFrom,
        to,
        setTo,
    } = useTableControls();

    const {
        page,
        setPage,
        isFetching,
        isLoading,
        data,
        refetch,
    } = useFetchData("promo-codes", getPromoCodes, [
        perPage,
        search_string,
        status,
        from,
        to,
    ]);

    const [showImageViewer, setShowImageViewer] = useState(false);
    const [viewerImages, setViewerImages] = useState([]);
    const [viewerStartIndex, setViewerStartIndex] = useState(0);

    const openImageViewer = (image) => {
        if (!image) return;
        setViewerImages([image]);
        setViewerStartIndex(0);
        setShowImageViewer(true);
    };

    const { mutate: deleteMutation } = useMutation((id) => deletePromoCode(id), {
        onSuccess: (res) => {
            Success("Promo Code", res.data?.message || "Promo code deleted successfully");
            refetch();
        },
        onError: (err) => Error(err?.response?.data?.message || "Failed to delete promo code.")
    });

    const handleDelete = (id, promoCode) => {
        Confirmation(
            `Are you sure you want to delete the promo code "${promoCode}"? This action cannot be undone.`,
            "Yes, Delete",
            () => deleteMutation(id)
        );
    };

    const { mutate: statusMutation } = useMutation((id) => handlePromoCodeStatus(id), {
        onSuccess: (res) => {
            Success("Status Update", res.data?.message || "Promo code status updated");
            refetch();
        },
        onError: (err) => Error(err?.response?.data?.message || "Failed to update status.")
    });

    const toggleStatus = (item) => {
        Confirmation(
            `Are you sure you want to ${item.visibilityStatus === 'active' ? "deactivate" : "activate"} ${item.promoCode}?`,
            "Yes",
            () => statusMutation(item._id)
        );
    };

    return (
        <div className="app-content content">
            <div className="content-wrapper">
                <div className="content-body">
                    <section id="configuration">
                        <div className="row">
                            <div className="col-12">
                                <div className="user-details-wrapper">
                                    <PageHeader
                                        title="Promo Codes Management"
                                        subtitle="Create and manage promotional discount codes for your users"
                                        action={
                                            <Link to="/promo-codes/add" className="siteBtn header-btn-standard w-100-mobile">
                                                <i className="fas fa-plus me-2"></i> Add New Promo
                                            </Link>
                                        }
                                    />

                                    <Table
                                        key="promo-codes-table"
                                        tableTitles={[
                                            "S.No.",
                                            "Image",
                                            "Promo Code",
                                            "Discount Type",
                                            "Value",
                                            "Expiry Date",
                                            "Status",
                                            "Action"
                                        ]}
                                        dateFilter
                                        showEntries
                                        sortBy
                                        isLoading={isLoading}
                                        isFetching={isFetching}
                                        data={data?.data?.data?.docs}
                                        totalPages={Math.ceil(data?.data?.data?.totalPages || 0)}
                                        totalDocs={data?.data?.data?.totalDocs}
                                        page={page}

                                        setPage={setPage}
                                        setSearchString={setSearchString}
                                        status={status}
                                        setStatus={setStatus}
                                        perPage={perPage}
                                        setPerPage={setPerPage}
                                        from={from}
                                        setFrom={setFrom}
                                        to={to}
                                        setTo={setTo}
                                    >
                                        {data?.data?.data?.docs?.map((item, index) => (
                                                <tr key={index}>
                                                    <td>{getSerialNumber(data?.data?.data, index)}</td>
                                                    <td>
                                                        <div className="promo-thumb border rounded p-1 d-inline-block shadow-sm overflow-hidden" style={{ width: '60px', height: '40px', background: '#f8fafc' }}>
                                                            <img
                                                                src={item.promoBanner ? `${image_url}${item.promoBanner}` : "images/placeholder.png"}
                                                                alt={item.promoCode}
                                                                className="w-100 h-100 object-fit-cover rounded"
                                                                style={{ cursor: 'pointer' }}
                                                                onClick={() => openImageViewer(item.promoBanner ? `${image_url}${item.promoBanner}` : null)}
                                                            />
                                                        </div>
                                                    </td>
                                                    <td className="fw-bold text-primary">{item.promoCode}</td>
                                                    <td className="text-capitalize">{item.discountType}</td>
                                                    <td className="fw-bold">
                                                        {item.discountType === 'percentage' ? `${item.value}%` : `$${item.value}`}
                                                    </td>
                                                    <td>{item.expiryDate ? format_date(item.expiryDate, "MM/DD/YYYY") : "N/A"}</td>
                                                    <td>
                                                        <span className={`status-pill ${item.visibilityStatus === 'active' ? 'status-active' : 'status-inactive'}`}>
                                                            {item.visibilityStatus === 'active' ? "Active" : "Inactive"}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <StatusHandler
                                                            edit
                                                            active
                                                            item={{ ...item, active: item.visibilityStatus === "active" }}
                                                            redirectUrl={`/promo-codes/edit/${item._id}`}
                                                            onClick={() => toggleStatus(item)}
                                                            onDelete={() => handleDelete(item._id, item.promoCode)}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                    </Table>
                                </div>
                            </div>
                        </div>
                    </section>
                    <ModalImageViewer
                        show={showImageViewer}
                        images={viewerImages}
                        startIndex={viewerStartIndex}
                        onHide={() => setShowImageViewer(false)}
                        title="Promo Banner"
                    />
                </div>
            </div>
            <style>{`
                .fw-800 { font-weight: 800; }
                
                @media (max-width: 576px) {
                    .w-100-mobile { width: 100% !important; text-align: center; justify-content: center; }
                    .user-details-wrapper { padding: 0.5rem; }
                }
            `}</style>
        </div>
    );
}
